import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JsonApiResponse } from '@app/interfaces';
import { RL1_BOX_GROUP_NAMES, Rl1Box } from '@app/models/payroll/rl1-box.model';
import { RL1Summary } from '@app/models/payroll/rl1-summary.model';
import { Rl1 } from '@app/models/payroll/rl1.model';
import { AuthService, FileHelperService } from '@app/services';
import { TranslatableKey } from '@app/types/translatable.type';
import { groupBy, sortBy } from 'lodash-es';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PayrollResources } from '../payroll.resources';

@Injectable()
export class RL1Service {
    constructor(
        private auth: AuthService,
        private http: HttpClient,
        private fileHelper: FileHelperService
    ) {}

    async generateRL1Summary(rl1Summary?: RL1Summary): Promise<RL1Summary> {
        if (!rl1Summary) {
            rl1Summary = new RL1Summary();
        }

        rl1Summary.defaultContact = {
            name: this.auth.account.fullName || '',
            email: this.auth.account.email,
            phoneNumber: this.auth.company.phonePrimary,
        };

        return await rl1Summary.param('company', this.auth.company.id).save();
    }

    /**
     * Retrieves all of the boxes on an RL1
     */
    getBoxes(taxReturn: Rl1): Observable<{ title?: TranslatableKey; boxes: Rl1Box[] }[]> {
        return from(Rl1Box.limit(100).get()).pipe(
            // RL-1s are grouped according to specific titles which need to be calculated here
            map(([boxes]) => groupBy(boxes, (box) => box.group)),
            map((boxes) =>
                RL1_BOX_GROUP_NAMES.map((key) => ({
                    title: `payroll.taxForms.RL1.employee.boxTitles.${key}`,
                    boxes: sortBy(boxes[key], (box) => taxReturn.errors[box.xmlNameCamel] == undefined),
                }))
            )
        );
    }

    async addAmendedRL1(year: number, prEmployeeId: number | string): Promise<Rl1> {
        const rl1 = new Rl1();
        rl1.year = year;
        rl1.employeeId = prEmployeeId.toString();

        return await rl1.param('company', this.auth.company.id).save();
    }

    async deleteRL1(rl1: Rl1): Promise<Rl1> {
        return await rl1.param('company', this.auth.company.id).delete();
    }

    /**
     * Reverts any fields modified by the user to its original generated state
     */
    revertToOriginalState(rl1: Rl1): Observable<Rl1> {
        const path = PayrollResources.RL1Revert.replace(':rl1', rl1.id.toString());

        return this.http.put<Rl1>(path, {});
    }

    issue(summary: RL1Summary, manualSubmit: boolean): Observable<RL1Summary> {
        const url = `/v2/payroll/companies/${this.auth.company.id}/rl1Summaries/${summary.id}/issue`;

        return this.http
            .put<JsonApiResponse<RL1Summary>>(url, { data: { attributes: { manualSubmit } } })
            .pipe(map((response) => new RL1Summary(response.data.attributes)));
    }

    async downloadTaxReturnPreview(rl1Summary: RL1Summary): Promise<void> {
        return new Promise((resolve, reject) => {
            const url = `${this.auth.company.id}/rl1Summaries/${rl1Summary.id}/downloadPdfPreview`;

            this.fileHelper.savePayrollFile(url, `rl1_summary_pdf_preview_${rl1Summary.year}`, 'pdf', null, (e) =>
                e ? reject(e) : resolve()
            );
        });
    }

    async downloadTaxFormPreview(rl1: Rl1): Promise<void> {
        return new Promise((resolve, reject) => {
            const url = `/v2/payroll/rl1s/${rl1.id}/downloadPdfPreview`;

            this.fileHelper.savePayrollFile(url, `rl1_pdf_preview_${rl1.year}_${rl1.id}`, 'pdf', null, (e) =>
                e ? reject(e) : resolve()
            );
        });
    }

    async downloadTaxForm(rl1: Rl1): Promise<void> {
        return new Promise((resolve, reject) => {
            const url = `/v2/payroll/rl1s/${rl1.id}/download`;

            this.fileHelper.savePayrollFile(url, `Rl1_${rl1.firstName}_${rl1.lastName}`, 'pdf', null, (e) =>
                e ? reject(e) : resolve()
            );
        });
    }

    async downloadTaxPackage(rl1Summary: RL1Summary): Promise<void> {
        return new Promise((resolve, reject) => {
            const year = rl1Summary.year;
            const url = `${this.auth.company.id}/rl1Summaries/${rl1Summary.id}/download`;

            this.fileHelper.savePayrollFile(url, `RL1_${year.toString()}`, 'zip', { year }, (e) =>
                e ? reject(e) : resolve()
            );
        });
    }

    sendBulkEmails(rl1Summary: RL1Summary): Promise<RL1Summary> {
        return this.http
            .put<RL1Summary>(
                `/v2/payroll/companies/${this.auth.company.id}/rl1Summaries/${rl1Summary.id}/bulkEmail`,
                {}
            )
            .toPromise();
    }

    sendRl1Email(rl1: Rl1): Promise<Rl1> {
        return this.http.get<Rl1>(PayrollResources.Rl1Email.replace(':rl1', rl1.id.toString())).toPromise();
    }
}
